import { useMemo } from 'react';
import { pick } from 'js/utils/ramda.util';
import { useUserSelector, useAppSelector } from 'js/store/hooks';

import { imsToFieldValue } from '../helpers';
import { ADDRESS_DETAILS_KEYS, PLAYER_INFO_DIFFERENCE_MAP } from '../constants';

export const useInitialValues = ({ fields, playerTags }) => {
    const { userDetails } = useUserSelector(pick(['userDetails']));
    const { allowedCurrencies, allowedCountries } = useAppSelector(pick(['allowedCurrencies', 'allowedCountries']));

    return useMemo(() => {
        // map keys from GetPlayerInfo.DataMap to UpdatePlayerInfo.DataMap
        const details = Object.keys(userDetails)
            .reduce((accumulator, key) => {
                switch (key) {
                    case 'addressDetails': {
                        for (const addressKey of ADDRESS_DETAILS_KEYS) {
                            accumulator[addressKey] = userDetails.addressDetails[addressKey];
                        }

                        break;
                    }

                    case 'password':
                    case 'authenticationPIN':
                        break;

                    default:
                        accumulator[key] = userDetails[key];
                }

                if (PLAYER_INFO_DIFFERENCE_MAP[key]) {
                    accumulator[PLAYER_INFO_DIFFERENCE_MAP[key]] = userDetails[key];
                    delete accumulator[key];
                }

                return accumulator;
            }, {});
        const tags = playerTags?.reduce((accumulator, { name, value }) => ({
            ...accumulator,
            [name]: value
        }), {});
        const additionalData = { allowedCurrencies, allowedCountries };

        return fields
            .filter((field) => field.imsField || field.usePlayerTags)
            .reduce((accumulator, field) => {
                const tag = field.usePlayerTags && field.playerTags[0]?.name;

                if (tag && tag in tags) {
                    const fieldValue = imsToFieldValue(tags[tag], field, additionalData);

                    if (fieldValue !== null) {
                        accumulator[field.name] = fieldValue;
                    }
                } else if (field.imsField in details) {
                    accumulator[field.name] = imsToFieldValue(details[field.imsField], field, additionalData);
                }

                return accumulator;
            }, {});
    }, [fields, userDetails, playerTags, allowedCurrencies, allowedCountries]);
};
